import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  TrendingDown,
  CalendarX,
  Target,
  Clock,
  Rocket,
  Gauge,
  Unplug,
} from "lucide-react";

const challenges = [
  {
    icon: Users,
    title: "Staff is unreliable",
    text: "Turnover, sick days, and ramp time leave the desk understaffed — calls, verifications, and follow-ups slip.",
  },
  {
    icon: TrendingDown,
    title: "Revenue cycle is leaking",
    text: "Denied claims, underpayments, aging AR — money you already earned that never hits the bank.",
  },
  {
    icon: CalendarX,
    title: "Schedule isn't filling up",
    text: "No-shows, open blocks, manual rebooking — chairs sit empty while production walks out the door.",
  },
  {
    icon: Target,
    title: "Low case acceptance & recall",
    text: "Patients leave without accepting treatment, and 35–45% never come back for hygiene.",
  },
  {
    icon: Clock,
    title: "My staff can't keep up with the volume of inbound and outbound calls",
    text: "Missed calls, long hold times, and voicemails that never get returned — every unanswered call is a lost patient.",
  },
  {
    icon: Rocket,
    title: "The office isn't growing",
    text: "Stuck in firefighting mode — no bandwidth to add a chair, open a second location, or invest in marketing.",
  },
  {
    icon: Gauge,
    title: "Everything feels operationally taxing",
    text: "The practice runs you instead of the other way around — ops drain leaves no room to lead.",
  },
  {
    icon: Unplug,
    title: "Your software feels disconnected",
    text: "10+ tools that don't talk to each other — double entry, broken handoffs, no single source of truth.",
  },
];

const ProblemResonanceSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1300px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-6 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]"
          >
            What are you{" "}
            <span className="text-blue-600">here to solve?</span>
          </motion.h2>
        </div>

        {/* 4x2 card grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {challenges.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.title}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1 + i * 0.06 }}
                className="bg-white rounded-2xl border border-slate-200 shadow-[0_14px_40px_-18px_rgba(15,23,42,0.12)] p-5 flex flex-col hover:border-blue-300 hover:shadow-[0_18px_50px_-18px_rgba(59,130,246,0.25)] transition-all"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-3 flex-shrink-0">
                  <Icon className="w-5 h-5" strokeWidth={2.2} />
                </div>
                <h3 className="text-[15.5px] font-bold text-slate-900 leading-tight mb-1.5">
                  {c.title}
                </h3>
                <p className="text-[12.5px] text-slate-600 leading-[1.55]">
                  {c.text}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProblemResonanceSlide;
